import type Phaser from 'phaser';
import { foamSpeed, type Course } from '../sim/course';
import type { SimEvent, SimState } from '../sim/sim';
import { TUNING } from '../sim/tuning';
import { PALETTE } from './palette';

/**
 * チュートリアル。**図形で見せて、一言だけ添える。**
 *
 * 以前はここで文字をいっさい使わない方針を取っていた。6 歳から遊べることを
 * 狙っているので読ませたくない、というのが理由だった。
 * **実際に初めて遊んだ人から「何をすればいいか分からなかった」と返ってきて、
 * その方針は落ちた。** 図形だけでは「これは自分が操作するものだ」という
 * ところが伝わらない。図形は変えずに、**そのとき出ている合図にだけ**
 * 一言を添える形にした（合図が消えれば文字も消える）。
 *
 * 添えるのは 1 行だけ。読まなくても図形で分かる状態は保つ。
 *
 * 教え方の中心は「輪が閉じた瞬間にタップ」。崩れの先頭が届くまでの時間を
 * 縮む輪の半径で表すので、**タイミングそのものを図形で見せられる**。
 * 文章で「直前に潜れ」と言うより正確に伝わる。
 *
 * シミュレーションには一切触らない。表示するのは sim の状態から読み取った事実だけで、
 * リプレイの決定論を壊さない（チュートリアル中の走行も普通に記録・再生できる）。
 */

export type Lesson = 'steer' | 'dive' | 'ride';

const KEY = 'etw:tutorial';
const ALL: readonly Lesson[] = ['steer', 'dive', 'ride'];

/** 横に合計これだけ動けば、舵の取り方は分かったと見なす（m）。 */
const STEER_LEARNED_M = 3.5;
/** 崩れの先頭が届くまでこの時間を切ったら、ダイブの合図を出す（秒）。 */
const DIVE_CUE_LEAD = 2.2;

export interface TutorialFrame {
  readonly sim: SimState;
  readonly course: Course;
  readonly screenX: (worldX: number) => number;
  readonly screenY: (worldY: number) => number;
  readonly anchorY: number;
  readonly pxPerMeter: number;
  readonly timeMs: number;
}

export class Tutorial {
  private done: Record<Lesson, boolean>;
  private lateralTravel = 0;
  private lastX: number | null = null;

  constructor() {
    this.done = { steer: false, dive: false, ride: false };
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) Object.assign(this.done, JSON.parse(raw) as Partial<Record<Lesson, boolean>>);
    } catch {
      // 読めなければ、毎回教えるだけ。害はない。
    }
  }

  /** まだ教えることが残っているか。 */
  get active(): boolean {
    return ALL.some((lesson) => !this.done[lesson]);
  }

  /** 走行の開始時に呼ぶ。 */
  reset(): void {
    this.lateralTravel = 0;
    this.lastX = null;
  }

  private complete(lesson: Lesson): void {
    if (this.done[lesson]) return;
    this.done[lesson] = true;
    try {
      localStorage.setItem(KEY, JSON.stringify(this.done));
    } catch {
      // 保存できなくても、この走行のあいだは覚えている。
    }
  }

  /**
   * 習得判定。できたことは二度と教えない。
   * @returns このフレームで習得したもの（演出のきっかけに使う）
   */
  update(sim: SimState, events: readonly SimEvent[]): Lesson | null {
    let learned: Lesson | null = null;

    if (!this.done.steer) {
      if (this.lastX !== null) this.lateralTravel += Math.abs(sim.x - this.lastX);
      if (this.lateralTravel >= STEER_LEARNED_M) {
        this.complete('steer');
        learned = 'steer';
      }
    }
    this.lastX = sim.x;

    for (const event of events) {
      // 潜れたか（安全に抜けられたか）で判定する。押せたこと自体が第一歩。
      if (event.kind === 'dive' && !this.done.dive) {
        this.complete('dive');
        learned ??= 'dive';
      }
      if (event.kind === 'ride' && !this.done.ride) {
        this.complete('ride');
        learned ??= 'ride';
      }
    }

    return learned;
  }

  /**
   * 合図を描く。**一度に 1 つだけ**。
   * 複数出すと、どれに反応すべきか分からなくなる。
   * 急を要するものから順に見る。
   */
  draw(g: Phaser.GameObjects.Graphics, frame: TutorialFrame): Lesson | null {
    if (!this.active) return null;
    if (!this.done.dive && this.drawDiveCue(g, frame)) return 'dive';
    if (!this.done.ride && this.drawRideCue(g, frame)) return 'ride';
    if (!this.done.steer) {
      this.drawSteerCue(g, frame);
      return 'steer';
    }
    return null;
  }

  /**
   * 崩れの先頭が届くまでの時間を、縮む輪で見せる。
   * 輪が甲羅の大きさまで閉じた瞬間がタップの時。
   */
  private drawDiveCue(g: Phaser.GameObjects.Graphics, frame: TutorialFrame): boolean {
    const { sim, course, anchorY, timeMs } = frame;
    const x = frame.screenX(sim.x);

    let best = Infinity;
    for (const swell of sim.swells) {
      if (!swell.broken || swell.settled) continue;
      const gap = swell.y - sim.y;
      if (gap <= 0) continue;
      const closing = Math.max(0.5, foamSpeed(course.stage, swell.y) - Math.max(0, sim.speed));
      const tti = (gap - TUNING.foamHitHalf) / closing;
      if (tti > 0 && tti < best) best = tti;
    }
    if (best === Infinity || best > DIVE_CUE_LEAD) return false;

    const t = best / DIVE_CUE_LEAD;
    const radius = 20 + t * 46;
    const urgent = best <= TUNING.dolphinWindow;

    g.lineStyle(urgent ? 3.5 : 2.5, urgent ? PALETTE.foam : PALETTE.hint, urgent ? 1 : 0.85);
    g.strokeCircle(x, anchorY, radius);
    // 内側の目安。ここまで縮んだら押す。
    g.lineStyle(1.5, PALETTE.hint, 0.4);
    g.strokeCircle(x, anchorY, 20);

    // タップの絵。指先の波紋で「押す」を示す。
    const glyphY = anchorY + 62;
    const pulse = (timeMs % 700) / 700;
    g.fillStyle(PALETTE.hint, 0.9);
    g.fillCircle(x, glyphY, 5);
    g.lineStyle(2, PALETTE.hint, 0.7 * (1 - pulse));
    g.strokeCircle(x, glyphY, 6 + pulse * 13);

    // 下向きの山形。潜る方向を示す。
    g.lineStyle(3, PALETTE.hint, urgent ? 1 : 0.7);
    for (let i = 0; i < 2; i++) {
      const cy = anchorY + 26 + i * 9;
      g.beginPath();
      g.moveTo(x - 9, cy);
      g.lineTo(x, cy + 6);
      g.lineTo(x + 9, cy);
      g.strokePath();
    }
    return true;
  }

  /** 乗れる斜面を光らせ、上向きの山形で「ここに居ろ」を示す。 */
  private drawRideCue(g: Phaser.GameObjects.Graphics, frame: TutorialFrame): boolean {
    const { sim, timeMs } = frame;

    let target: { y: number } | null = null;
    for (const swell of sim.swells) {
      if (swell.broken) continue;
      const gap = swell.y - sim.y;
      if (gap > -1 && gap <= TUNING.faceWindow + 7) {
        if (!target || gap < target.y) target = { y: swell.y };
      }
    }
    if (!target) return false;

    const crestY = frame.screenY(target.y);
    const faceTop = frame.screenY(target.y - TUNING.faceWindow);
    g.lineStyle(2, PALETTE.hint, 0.5 + 0.3 * Math.sin(timeMs / 180));
    g.strokeRect(2, faceTop, 536, Math.max(4, crestY - faceTop));

    g.lineStyle(3, PALETTE.hint, 0.85);
    for (let i = 0; i < 3; i++) {
      const cx = 100 + i * 170;
      const cy = faceTop + (crestY - faceTop) * 0.55 + Math.sin(timeMs / 200 + i) * 3;
      g.beginPath();
      g.moveTo(cx - 11, cy + 6);
      g.lineTo(cx, cy - 5);
      g.lineTo(cx + 11, cy + 6);
      g.strokePath();
    }
    return true;
  }

  /** 指を置いて左右に動かす絵。 */
  private drawSteerCue(g: Phaser.GameObjects.Graphics, frame: TutorialFrame): void {
    const { sim, anchorY, timeMs } = frame;
    const x = frame.screenX(sim.x);
    const swing = Math.sin(timeMs / 620);
    const glyphY = anchorY + 68;
    const fingerX = x + swing * 34;

    // 指先と、そこから伸びる軌跡。
    g.lineStyle(2, PALETTE.hint, 0.3);
    g.beginPath();
    g.moveTo(x - 38, glyphY);
    g.lineTo(x + 38, glyphY);
    g.strokePath();

    g.fillStyle(PALETTE.hint, 0.95);
    g.fillCircle(fingerX, glyphY, 7);
    g.lineStyle(2, PALETTE.hint, 0.5);
    g.strokeCircle(fingerX, glyphY, 12);

    // 左右の山形。動かす向きを示す。
    g.lineStyle(3, PALETTE.hint, 0.8);
    for (const side of [-1, 1]) {
      const cx = x + side * 52;
      g.beginPath();
      g.moveTo(cx - side * 6, glyphY - 8);
      g.lineTo(cx + side * 5, glyphY);
      g.lineTo(cx - side * 6, glyphY + 8);
      g.strokePath();
    }
  }
}
